import type { Dispatch, SetStateAction } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  NotificationMessage,
  NotificationsQueue,
} from '@/components/Notifications';
import { Endpoint, QueryKey } from '@/constants';
import type { ValidationErrors } from '@/types';
import { postData } from '@/utils/postData';

export const useAddDiscountCode = (
  handleSuccess: () => void,
  handleErrors: Dispatch<SetStateAction<ValidationErrors>>,
  onSuccessCallback?: (discountCode: string) => void,
) => {
  const queryClient = useQueryClient();

  const { isPending, mutate } = useMutation({
    mutationFn: postData(Endpoint.AddDiscountCode),
    onError: error => {
      console.error(`Error applying discount code: ${error}`);
    },
    onSuccess: (data, variables) => {
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={!data.isSuccess}>
              {data.messages[0]}
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );

      if (data.isSuccess) {
        queryClient.invalidateQueries({ queryKey: [QueryKey.GetCart] });

        handleSuccess();

        // Call the tracking callback if provided
        if (onSuccessCallback && variables && typeof variables === 'object' && 'discountCode' in variables) {
          const discountCode = String(variables.discountCode);
          onSuccessCallback(discountCode);
        }
      } else {
        handleErrors({ discountCode: data.messages[0] });
      }
    },
  });

  return { addDiscountCode: mutate, isPending };
};
